import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SwipeConfirm } from "@/components/SwipeConfirm";
import { useApp } from "@/context/AppContext";
import { CurrencyFlag } from "@/components/CurrencyFlag";
import { fmtCcy } from "@/lib/format";
import { AlertTriangle, Info, Settings2, TrendingUp } from "lucide-react";

const CHIPS = { USD: [100, 500, 1000, 5000], default: [500, 1000, 5000, 10000] };
const num = (n, d = 4) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: d });

/** Buying, reduced to one decision: how much do you want to spend? */
export const QuickBuy = ({ open, onOpenChange, symbol, source = "global", onDone, onMoreOptions }) => {
  const { user, t } = useApp();
  const ccy = (user?.base_currency || "USD").toUpperCase();
  const chips = CHIPS[ccy] || CHIPS.default;
  const [quote, setQuote] = useState(null);
  const [amount, setAmount] = useState("");
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !symbol) return;
    setAmount(""); setPreview(null);
    let active = true;
    const load = () => api.get(`/market/quote/${symbol}`)
      .then(({ data }) => active && setQuote(data))
      .catch((e) => console.error("Couldn't load the price:", e));
    load();
    const iv = setInterval(load, 5000);
    return () => { active = false; clearInterval(iv); };
  }, [open, symbol]);

  useEffect(() => {
    const value = Number(amount);
    if (!open || !value || value <= 0) { setPreview(null); return; }
    setChecking(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/orders/preview", {
          symbol, side: "buy", order_type: "market", source,
          notional_amount: value, notional_currency: ccy,
        }, { signal: controller.signal });
        setPreview(data);
      } catch (e) {
        if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
        setPreview({ blockers: [e?.response?.data?.detail || "We couldn't price that amount."],
                     notes: [], can_submit: false });
      } finally { setChecking(false); }
    }, 450);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [amount, open, symbol, source, ccy]);

  const faceIdPayload = useMemo(
    () => ({ symbol, side: "buy", qty: preview?.qty }), [symbol, preview?.qty]);

  const buy = async (approvalToken) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        symbol, side: "buy", order_type: "market", source,
        notional_amount: Number(amount), notional_currency: ccy,
        approval_token: approvalToken || null,
      });
      toast.success(`Bought ${num(data.order.qty)} ${symbol}`, {
        description: `${fmtCcy(Number(amount), ccy)} invested at ${fmtCcy(data.order.fill_price, quote.currency)} a share`
          + `${approvalToken ? " · approved with Face ID" : ""}`,
        duration: 4000,
      });
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error("Order not placed", { description: e?.response?.data?.detail || "Please try again." });
    } finally { setSubmitting(false); }
  };

  if (!quote) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-buy">
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-2">
            {t("qb_buy")} {symbol}
            <span className="text-sm font-normal text-muted-foreground truncate">{quote.name}</span>
          </DialogTitle>
          <div className="flex items-center gap-2 text-sm">
            <span className="numeric font-semibold">{fmtCcy(quote.price, quote.currency)}</span>
            <span className={`numeric text-xs font-medium ${quote.change_pct >= 0 ? "text-emerald-600" : "text-destructive"}`}>
              {quote.change_pct >= 0 ? "+" : ""}{num(quote.change_pct, 2)}% {t("qb_today")}
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">{t("qb_how_much")}</label>
          <div className="flex items-center gap-2.5 rounded-2xl border-2 border-input focus-within:border-[color:var(--tharwy-lime)] px-3.5 py-3 transition-colors">
            <CurrencyFlag code={ccy} size={28} />
            <span className="text-sm font-semibold text-muted-foreground">{ccy}</span>
            <input type="number" inputMode="decimal" value={amount} placeholder="0"
              onChange={(e) => setAmount(e.target.value)} autoFocus
              data-testid="quick-buy-amount-input"
              className="flex-1 min-w-0 bg-transparent text-end text-2xl font-bold outline-none numeric" />
          </div>
          <div className="flex gap-1.5">
            {chips.map((c) => (
              <button key={c} type="button" onClick={() => setAmount(String(c))}
                data-testid={`quick-buy-chip-${c}`}
                className={`flex-1 rounded-full border py-1.5 text-xs font-semibold transition-colors ${
                  Number(amount) === c ? "border-[color:var(--tharwy-lime)] bg-secondary" : "hover:bg-secondary"}`}>
                {c.toLocaleString("en-US")}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-secondary p-3.5 text-sm space-y-1.5" data-testid="quick-buy-estimate">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" />{t("qb_you_get")}
            </span>
            <span className="numeric font-semibold">
              {preview ? `${num(preview.qty)} ${preview.unit_label || t("qb_shares")}` : "—"}
            </span>
          </div>
          {preview?.lines?.slice(-2).map((l) => (
            <div key={l.label} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{l.label}</span>
              <span className="numeric">{fmtCcy(l.value, l.currency)}</span>
            </div>
          ))}
          {checking && <div className="text-xs text-muted-foreground">{t("qb_checking")}</div>}
          {quote.currency !== ccy && (
            <p className="text-[11px] text-muted-foreground pt-0.5" data-testid="quick-buy-settlement">
              {symbol} {t("qb_settles")} {quote.currency} — {ccy} → {quote.currency}
            </p>
          )}
        </div>

        {(preview?.notes || []).slice(0, 2).map((n) => (
          <div key={n} className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed"
            data-testid="quick-buy-note">
            <Info className="h-3.5 w-3.5 mt-px shrink-0" /><span>{n}</span>
          </div>
        ))}
        {(preview?.blockers || []).map((b) => (
          <div key={b} className="flex items-start gap-2 rounded-lg bg-destructive/10 p-2.5 text-xs font-medium text-destructive leading-relaxed"
            data-testid="quick-buy-blocker">
            <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" /><span>{b}</span>
          </div>
        ))}

        <SwipeConfirm label={`${t("qb_swipe")} ${amount ? fmtCcy(Number(amount), ccy) : ""}`.trim()}
          tone="buy" submitting={submitting}
          disabled={!preview?.can_submit || checking}
          faceIdPayload={faceIdPayload}
          onConfirm={buy} />

        <Button variant="ghost" size="sm" className="w-full text-muted-foreground"
          onClick={onMoreOptions} data-testid="quick-buy-more-options">
          <Settings2 className="h-4 w-4 me-1.5" />{t("qb_more_options")}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
